import { City } from "../types/entities/city"
import { Cinema } from "../types/entities/cinema"

export type GeoPoint = { lat: number; lng: number }

export function parseGeo(geo?: { latitude?: string; longitude?: string }): GeoPoint | null {
    if (!geo?.latitude || !geo?.longitude) return null
    const lat = Number(geo.latitude)
    const lng = Number(geo.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return { lat, lng }
}

function haversineKm(a: GeoPoint, b: GeoPoint): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

export function findNearestCinema(
    userPos: GeoPoint,
    cities: City[],
    cinemas: Cinema[],
): { city: City; cinema: Cinema } | null {
    let best: { city: City; cinema: Cinema; dist: number } | null = null

    for (const cinema of cinemas) {
        const city = cities.find(c => c.id === cinema.city_id)
        const geo = parseGeo(cinema.geo) ?? parseGeo(city?.geo)
        if (!geo) continue

        const dist = haversineKm(userPos, geo)
        if (!best || dist < best.dist) {
            if (city) best = { city, cinema, dist }
        }
    }

    return best ? { city: best.city, cinema: best.cinema } : null
}

export function getCurrentPosition(timeoutMs = 8000): Promise<GeoPoint | null> {
    return new Promise(resolve => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
            resolve(null)
            return
        }

        const timer = setTimeout(() => resolve(null), timeoutMs)

        try {
            navigator.geolocation.getCurrentPosition(
                pos => {
                    clearTimeout(timer)
                    resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude })
                },
                () => {
                    clearTimeout(timer)
                    resolve(null)
                },
                { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 600_000 },
            )
        } catch {
            clearTimeout(timer)
            resolve(null)
        }
    })
}
